import React, {useEffect, useState} from 'react';
import {useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {DataGroupDto} from "../../../../../modules/account/dto/DataGroupDto";
import {DataGroupApi} from "../../../../../modules/account/api/DataGroupApi";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import {Dialog, DialogActions, DialogContent, DialogTitle,} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import CancelButton from "../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../components/buttons/SaveButton";
import DataGroupSelectDialog from "./DataGroupSelectDialog";
import SearchIconButton from '../../../../../components/buttons/SearchIconButton';
import CloseIconButton from "../../../../../components/buttons/CloseIconButton";

export type DataGroupDetailDialogProps = {
    open: boolean;
    dataGroup: DataGroupDto;
    dataGroups: Array<DataGroupDto>;
    dataGroupMap: Map<string, DataGroupDto>;
    onClose: () => void;
    onOk: () => void;
};

const DataGroupDetailDialog: React.FC<DataGroupDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.dataGroup.id ? 'ACCOUNT_DATA_GROUP_MODIFY' : 'ACCOUNT_DATA_GROUP_CREATE';

    const [innerDataGroup, setInnerDataGroup] = useState<DataGroupDto>(props.dataGroup);

    const [openSelectDialog, setOpenSelectDialog] = useState(false);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    useEffect(() => {
        if (props.open) {
            setInnerDataGroup({...props.dataGroup});
            setMessageMap(new Map<string, string>());
        }
    }, [props.open]);

    const _handleChange = (name: string, value: any) => {
        setInnerDataGroup(previousDataGroup => ({...previousDataGroup, [name]: value}));
    };

    const _handleOk = async () => {
        if (_validate()) {
            if (props.dataGroup.id) {
                const responseResult = await DataGroupApi.updateDataGroup(innerDataGroup);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'ACCOUNT_DATA_GROUP_MODIFY_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await DataGroupApi.createDataGroup(innerDataGroup);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'ACCOUNT_DATA_GROUP_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const _handleCancel = () => {
        props.onClose();
    };

    const _validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerDataGroup.code === '') {
            newMessageMap.set('code', intl.formatMessage({id: 'ACCOUNT_DATA_GROUP_CODE_REQUIRED'}));
        }
        if (innerDataGroup.name === '') {
            newMessageMap.set('name', intl.formatMessage({id: 'ACCOUNT_DATA_GROUP_NAME_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={_handleCancel} fullWidth={true}>
            <DialogTitle>
                {intl.formatMessage({id: title})}
            </DialogTitle>
            <DialogContent>
                <DataGroupSelectDialog dataGroups={props.dataGroups} open={openSelectDialog}
                                       onSelect={(value) => _handleChange("parentId", value)}
                                       onClose={() => setOpenSelectDialog(false)}/>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_DATA_GROUP_PARENT"})}
                               value={innerDataGroup.parentId && props.dataGroupMap.has(innerDataGroup.parentId) ? props.dataGroupMap.get(innerDataGroup.parentId)?.name : ''}
                               disabled={true}
                               endAdornment={
                                   <div>
                                       <SearchIconButton onClick={() => setOpenSelectDialog(true)}/>
                                       <CloseIconButton onClick={() => _handleChange("parentId", "")}/>
                                   </div>
                               }/>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_DATA_GROUP_CODE"})}
                               required={true} errorText={messageMap.get("code")}
                               value={innerDataGroup.code}
                               onChange={(value) => {
                                   _handleChange("code", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_DATA_GROUP_NAME"})}
                               required={true} errorText={messageMap.get("nameZh")}
                               value={innerDataGroup.name}
                               onChange={(value) => {
                                   _handleChange("name", value)
                               }}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={_handleCancel}/>
                <SaveButton onClick={_handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default DataGroupDetailDialog;
